import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { SITE_ID } from "../config/site";

/*
 * Who is signed in to the admin area, and how to talk to the API as them.
 *
 * The session lasts exactly as long as the API's sign-in token: its expiry is
 * read from the token itself, so the site cannot go on showing someone as
 * signed in after the API has stopped accepting them. A 401 from the API ends
 * the session the same way.
 *
 * Someone still at work is not cut off at that expiry: on activity, once the
 * token is past half its life or within two hours of expiring, it is swapped
 * for a fresh one (POST /authenticate/refresh). Tabs keep in step through the
 * storage event, so signing out in one signs out the others.
 */

const API_BASE = process.env.REACT_APP_API_ENDPOINT || "";
const FUNCTIONS_KEY = process.env.REACT_APP_FUNCTIONS_KEY;
const STORAGE_KEY = "pm-admin-session";
// At most one refresh attempt this often, so a busy page does not call the API
// on every click; and how close to expiry a token is refreshed however long it
// was issued for.
const REFRESH_MIN_INTERVAL = 10 * 60 * 1000;
const REFRESH_WITHIN = 2 * 60 * 60 * 1000;
// When this browser last signed out, in ms, shared between tabs. A refresh
// sent before then is about a session that has ended; writing its answer back
// would sign every tab in again.
const SIGNED_OUT_KEY = "pm-admin-signed-out-at";

const AuthContext = createContext(null);

function tokenPayload(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), "=")));
  } catch (e) {
    return {};
  }
}

// The JWT's expiry, in milliseconds. Null if it cannot be read.
function tokenExpiry(token) {
  const { exp } = tokenPayload(token);
  return typeof exp === "number" ? exp * 1000 : null;
}

// When the JWT was issued, in milliseconds. Null if it cannot be read. Tokens
// issued before the API recorded "iat" don't say; their lifetime is fixed (24
// days when kept signed in, else 8 hours), so it is worked back from the expiry.
function tokenIssued(token) {
  const { iat, exp, pc } = tokenPayload(token);
  if (typeof iat === "number") return iat * 1000;
  if (typeof exp !== "number") return null;
  return exp * 1000 - (pc === "1" ? 24 * 24 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000);
}

function readStoredSession() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!stored || !stored.token) return null;
    const expires = tokenExpiry(stored.token);
    if (!expires || expires <= Date.now()) return null;
    return stored;
  } catch (e) {
    return null;
  }
}

function writeStoredSession(session) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Private browsing or storage blocked: the session lasts until the tab closes.
  }
}

function readSignedOutAt() {
  try {
    const stored = parseInt(localStorage.getItem(SIGNED_OUT_KEY), 10);
    return Number.isFinite(stored) ? stored : 0;
  } catch (e) {
    return 0;
  }
}

function markSignedOut() {
  try {
    localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()));
  } catch (e) {
    // Storage blocked: there are no other tabs sharing this session to tell.
  }
}

export function AuthProvider({ children }) {
  const location = useLocation();
  const [session, setSession] = useState(readStoredSession);
  // Why the last session ended, when it was not the user's choice.
  const [endedReason, setEndedReason] = useState("");

  const user = session ? session.user : null;
  const token = session ? session.token : "";
  // For handlers that outlive a render (a refresh in flight, storage events).
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const refreshingRef = useRef(false);
  const lastRefreshAttemptRef = useRef(0);
  // Bumped when the session ends, so a refresh still in flight from before can
  // tell its answer is no longer wanted.
  const sessionGenerationRef = useRef(0);

  // The refs are cleared at once rather than at the next render, so no
  // refresh in flight can write its token back in between.
  const dropSession = useCallback(() => {
    tokenRef.current = "";
    sessionGenerationRef.current += 1;
    refreshingRef.current = false;
  }, []);

  const endSession = useCallback((reason = "") => {
    dropSession();
    markSignedOut();
    writeStoredSession(null);
    setSession(null);
    setEndedReason(reason);
  }, [dropSession]);

  // Sign out when the token expires, whether or not anything is clicked.
  useEffect(() => {
    if (!token) return undefined;
    const expires = tokenExpiry(token);
    if (!expires) return undefined;
    const wait = Math.max(0, expires - Date.now());
    // setTimeout tops out at about 24.8 days; a 24-day token fits.
    const timer = setTimeout(
      () => endSession("Your session has expired. Please sign in again."),
      Math.min(wait, 2147483647),
    );
    return () => clearTimeout(timer);
  }, [token, endSession]);

  /* Swap the token for a fresh one. The API answers as it does to a sign-in,
     { token, user }. A 401 means the session is over, so it is ended as an
     expired one is; anything else (no network, a 5xx) keeps the session to be
     tried again later -- the expiry timer above still ends it if no refresh
     gets through. That timer is keyed on the token, so it reschedules itself
     from the new one. */
  const refreshSession = useCallback(async () => {
    const current = tokenRef.current;
    if (!current || refreshingRef.current) return;
    const startedAt = Date.now();
    const generation = sessionGenerationRef.current;
    lastRefreshAttemptRef.current = startedAt;
    refreshingRef.current = true;
    // Signed out, or replaced by another tab, while the request was out. The
    // sign-out time is read from storage, not only heard through the storage
    // event, which can arrive after the answer does.
    const stale = () =>
      sessionGenerationRef.current !== generation ||
      tokenRef.current !== current ||
      readSignedOutAt() >= startedAt;
    try {
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${current}`,
      };
      if (FUNCTIONS_KEY) headers["x-functions-key"] = FUNCTIONS_KEY;
      const response = await fetch(`${API_BASE}/authenticate/refresh`, {
        method: "POST",
        headers,
        body: JSON.stringify({ websiteId: SITE_ID }),
      });
      if (stale()) return;
      if (response.status === 401) {
        endSession("Your session has expired. Please sign in again.");
        return;
      }
      if (!response.ok) return;
      const data = await response.json().catch(() => ({}));
      if (!data.token || stale()) return;
      setSession((prev) => {
        const next = { token: data.token, user: data.user || (prev && prev.user) };
        writeStoredSession(next);
        return next;
      });
    } catch (e) {
      // No network: keep the session and try again on later activity.
    } finally {
      // Ending the session already cleared this; a refresh begun since then
      // owns it now.
      if (sessionGenerationRef.current === generation) refreshingRef.current = false;
    }
  }, [endSession]);

  // Refresh if the token is far enough through its life. Runs on every click
  // and key press, so it returns early when there is nothing to do.
  const refreshIfDue = useCallback(() => {
    const current = tokenRef.current;
    if (!current || refreshingRef.current) return;
    if (Date.now() - lastRefreshAttemptRef.current < REFRESH_MIN_INTERVAL) return;
    const expires = tokenExpiry(current);
    const now = Date.now();
    if (!expires || expires <= now) return;
    const issued = tokenIssued(current);
    const pastHalfLife = issued !== null && now >= issued + (expires - issued) / 2;
    if (pastHalfLife || expires - now <= REFRESH_WITHIN) refreshSession();
  }, [refreshSession]);

  // Activity: moving between pages...
  useEffect(() => {
    refreshIfDue();
  }, [location.pathname, refreshIfDue]);

  // ...and clicks, key presses and coming back to the tab within one.
  useEffect(() => {
    if (!token) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshIfDue();
    };
    document.addEventListener("pointerdown", refreshIfDue, { passive: true });
    document.addEventListener("keydown", refreshIfDue);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("pointerdown", refreshIfDue);
      document.removeEventListener("keydown", refreshIfDue);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, refreshIfDue]);

  /* Keep tabs in step. A "storage" event reaches every other tab when the
     session is written: removed means signed out elsewhere, so end it here
     too (otherwise this tab stayed signed in and could write it back); a new
     token means a refresh or a sign-in elsewhere, so use it. */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea && e.storageArea !== localStorage) return;
      // key is null when another tab cleared localStorage altogether.
      if (e.key !== STORAGE_KEY && e.key !== null) return;
      const stored = readStoredSession();
      if (!stored) {
        if (tokenRef.current) {
          // Also abandons a refresh in flight here.
          dropSession();
          setSession(null);
          setEndedReason("");
        }
        return;
      }
      if (stored.token !== tokenRef.current) setSession(stored);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dropSession]);

  /** Calls the API. `path` is relative to the API base, e.g. "/users". */
  // Reads the token at call time, so apiFetch keeps one identity for the whole
  // session: when it changed on every refresh, the messages list reloaded and
  // dropped the older pages already loaded.
  const apiFetch = useCallback(
    async (path, options = {}) => {
      const token = tokenRef.current;
      const isFormData =
        typeof FormData !== "undefined" && options.body instanceof FormData;
      const headers = { Accept: "application/json", ...(options.headers || {}) };
      if (options.body && !isFormData && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }
      if (FUNCTIONS_KEY) headers["x-functions-key"] = FUNCTIONS_KEY;
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
      if (response.status === 401 && token) {
        endSession("Your session has ended. Please sign in again.");
      }
      return response;
    },
    [endSession],
  );

  const login = useCallback(async ({ email, password, remember }) => {
    try {
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      if (FUNCTIONS_KEY) headers["x-functions-key"] = FUNCTIONS_KEY;
      const response = await fetch(`${API_BASE}/Authenticate`, {
        method: "POST",
        headers,
        // "remember" asks the API for its long-lived token (24 days), not 8 hours.
        body: JSON.stringify({
          email: email.trim(),
          password,
          isPrivateComputer: Boolean(remember),
          websiteId: SITE_ID,
        }),
      });
      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        data = {};
      }
      // The API could not check the password (its database is down): say so,
      // rather than that the password was wrong.
      if (response.status === 503) {
        return {
          ok: false,
          error: data.message || "Sign-in is unavailable just now. Try again shortly.",
        };
      }
      if (!response.ok || !data.token) {
        return {
          ok: false,
          error:
            data.message === "Account is not active"
              ? "This account has not been activated yet."
              : "That email and password did not match an account.",
        };
      }
      const next = { token: data.token, user: data.user || { email } };
      writeStoredSession(next);
      setSession(next);
      setEndedReason("");
      lastRefreshAttemptRef.current = 0;
      return { ok: true };
    } catch (e) {
      return { ok: false, error: "Could not reach the server. Please try again." };
    }
  }, []);

  const logout = useCallback(() => endSession(""), [endSession]);

  const value = useMemo(
    () => ({ user, token, login, logout, endSession, apiFetch, endedReason }),
    [user, token, login, logout, endSession, apiFetch, endedReason],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/** True when the user holds any of the roles, compared without case. */
export function hasAnyRole(user, roles) {
  const held = (user && Array.isArray(user.roles) ? user.roles : []).map((r) =>
    String(r).toLowerCase(),
  );
  return roles.some((r) => held.includes(r.toLowerCase()));
}
